import type { PreserveModeId } from "./preserve-modes";
import type { TrelloLocalExportSession } from "./trello-reader";
import {
  readConnectorConfig,
  type TrelloAuthorizationOptions,
  type TrelloPowerUpGlobal,
  type TrelloPowerUpInitializeOptions,
  type TrelloRestApiClient,
} from "./trello-power-up";

export interface TrelloAuthorizationClient {
  authorize(mode: PreserveModeId): Promise<void>;
  clearBrowserAuthorization(): Promise<void>;
  createCloudContextSession(
    state:
      | "cloud-capture"
      | "cloud-capture-status"
      | "cloud-active-captures"
      | "cloud-compare"
      | "cloud-history"
      | "cloud-history-warnings"
      | "cloud-snapshot-delete"
      | "in-place-restore-preview"
      | "in-place-restore-prepare"
      | "in-place-restore-status"
      | "in-place-restore-confirm"
      | "in-place-restore-control"
      | "job-control"
      | "cloud-settings"
      | "cloud-disconnect"
      | "restore-preview"
      | "restore-confirm"
      | "restore-status"
      | "restore-import-declare"
      | "restore-import-upload"
      | "restore-import-validate"
      | "restore-import-status"
      | "restore-import-abort"
      | "restore-import-rebind"
      | "portal-handoff",
  ): Promise<TrelloCloudContextSession>;
  createCloudConnectionSession(): Promise<TrelloCloudConnectionSession>;
  createLocalExportSession(): Promise<TrelloLocalExportSession>;
  isAuthorized(): Promise<boolean>;
  resizeToContent(): Promise<void>;
}

export interface TrelloCloudConnectionSession {
  contextJwt: string;
  trelloToken: string;
}

export interface TrelloCloudContextSession {
  contextJwt: string;
}

export type TrelloAuthorizationAvailability =
  "available" | "client_unavailable" | "unconfigured";

export interface TrelloAuthorizationSetup {
  availability: TrelloAuthorizationAvailability;
  client: TrelloAuthorizationClient | null;
}

export function authorizationOptionsForMode(
  mode: PreserveModeId,
): TrelloAuthorizationOptions {
  return {
    expiration: "never",
    scope: mode === "local" ? "read" : "read,write",
  };
}

export function createTrelloAuthorizationSetup(
  powerUp: TrelloPowerUpGlobal | undefined,
  apiKey: string | undefined,
  origin: string,
): TrelloAuthorizationSetup {
  const config = readConnectorConfig(apiKey, origin);

  if (!config) {
    return { availability: "unconfigured", client: null };
  }

  if (!powerUp) {
    return { availability: "client_unavailable", client: null };
  }

  const identity: TrelloPowerUpInitializeOptions = {
    appAuthor: config.appAuthor,
    appKey: config.appKey,
    appName: config.appName,
  };
  const context = powerUp.iframe(identity);
  context.render(() => undefined);

  async function restApi(): Promise<TrelloRestApiClient> {
    return context.getRestApi();
  }

  return {
    availability: "available",
    client: {
      async authorize(mode): Promise<void> {
        const client = await restApi();
        if (mode === "cloud") {
          // The Trello client persists one opaque token but does not expose its
          // scopes. A token first granted for Local Export may therefore be
          // read-only; force fresh consent before managed write operations.
          await client.clearToken();
        }
        await client.authorize(authorizationOptionsForMode(mode));
      },
      async clearBrowserAuthorization(): Promise<void> {
        await (await restApi()).clearToken();
      },
      async createLocalExportSession(): Promise<TrelloLocalExportSession> {
        const [board, token] = await Promise.all([
          context.board("id"),
          (await restApi()).getToken(),
        ]);

        if (!token) {
          throw new Error("Trello authorization is required.");
        }

        return {
          apiKey: config.appKey,
          boardId: board.id,
          token,
        };
      },
      async createCloudContextSession(
        state,
      ): Promise<TrelloCloudContextSession> {
        return { contextJwt: await context.jwt({ state }) };
      },
      async createCloudConnectionSession(): Promise<TrelloCloudConnectionSession> {
        const [contextJwt, token] = await Promise.all([
          context.jwt({ state: "cloud-connect" }),
          (await restApi()).getToken(),
        ]);

        if (!token) {
          throw new Error("Trello authorization is required.");
        }

        return { contextJwt, trelloToken: token };
      },
      async isAuthorized(): Promise<boolean> {
        return (await restApi()).isAuthorized();
      },
      async resizeToContent(): Promise<void> {
        await context.sizeTo(".capture-card");
      },
    },
  };
}
